import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService, User } from '../services/authService';
import { LoginRequest, RegisterRequest } from '../services/api';
import { localStorageUtils } from '../utils/localStorage';
import SyncConfirmModal from '../components/SyncConfirmModal';

/** 빈 객체/빈 배열은 "데이터 없음"으로 간주 */
function hasMeaningfulSettings(obj: any): boolean {
  if (obj == null) return false;
  if (Array.isArray(obj)) return obj.length > 0;
  return typeof obj === 'object' && Object.keys(obj).length > 0;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<boolean>;
  register: (userData: RegisterRequest) => Promise<boolean>;
  webauthnLogin: (username: string) => Promise<boolean>;
  webauthnRegister: (userData: {
    username: string;
    email: string;
    name: string;
    nickname: string;
    phone?: string;
    password?: string;
    deviceName?: string;
  }) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: (updatedUser?: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [pendingLoginResolve, setPendingLoginResolve] = useState<((value: boolean) => void) | null>(null);

  // 초기 인증 상태 확인
  useEffect(() => {
    if (isInitialized) return;
    
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        
        // 쿠키 기반이므로 서버에서 사용자 정보를 가져옴
        // 쿠키가 있으면 사용자 정보를 반환하고, 없으면 401 에러 발생
        const currentUser = await authService.fetchCurrentUser();
        if (currentUser) {
          console.log('초기화: 사용자 정보 로드 성공', currentUser.username);
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          console.log('초기화: 인증되지 않음 (쿠키 없음 또는 만료)');
          setUser(null);
          setIsAuthenticated(false);
        }
        setIsInitialized(true);
      } catch (error: any) {
        console.error('인증 초기화 오류:', error);
        // 401 에러는 정상 (인증되지 않음)
        if (error.response?.status === 401) {
          console.log('초기화: 인증되지 않음 (401)');
          setUser(null);
          setIsAuthenticated(false);
        } else {
          setError('인증 상태 확인 중 오류가 발생했습니다.');
          setUser(null);
          setIsAuthenticated(false);
        }
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isInitialized]);

  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.login(credentials);
      
      if (result.success && result.user) {
        console.log('로그인 성공:', result.user);
        
        // 로컬스토리지에 데이터가 있는지 확인
        const localInvestments = localStorageUtils.getInvestments();
        const localPortfolioSettings = localStorageUtils.getPortfolioSettings();
        const localAssetSettings = localStorageUtils.getAssetSettings();
        const localFixedIncomes = localStorageUtils.getFixedIncomes();
        const localFixedExpenses = localStorageUtils.getFixedExpenses();
        const localMonthlyBalances = localStorageUtils.getMonthlyBalances();
        
        const hasLocalData = 
          localInvestments.length > 0 ||
          hasMeaningfulSettings(localPortfolioSettings) ||
          hasMeaningfulSettings(localAssetSettings) ||
          localFixedIncomes.length > 0 ||
          localFixedExpenses.length > 0 ||
          localMonthlyBalances.length > 0;
        
        if (hasLocalData) {
          // 로컬 데이터가 있으면 동기화 확인 모달 표시
          // 사용자 정보는 먼저 설정 (모달 표시를 위해)
          setUser(result.user);
          setIsAuthenticated(true);
          setIsInitialized(true);
          
          // 모달에서 동기화 여부를 결정할 때까지 대기
          return new Promise<boolean>((resolve) => {
            setPendingLoginResolve(() => resolve);
            setShowSyncModal(true);
          });
        }
        
        // 로컬 데이터가 없으면 바로 로그인 완료
        setUser(result.user);
        setIsAuthenticated(true);
        setIsInitialized(true);
        return true;
      } else {
        setError(result.error || '로그인에 실패했습니다.');
        return false;
      }
    } catch (error: any) {
      console.error('로그인 오류:', error);
      setError('로그인 중 오류가 발생했습니다.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (userData: RegisterRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.register(userData);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || '회원가입에 실패했습니다.');
        return false;
      }
    } catch (error: any) {
      console.error('회원가입 오류:', error);
      setError('회원가입 중 오류가 발생했습니다.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setIsInitialized(false);
      
      // 로그아웃 시 DB 데이터는 표시하지 않고 로컬스토리지 데이터만 표시
      // (이미 로컬스토리지에 저장되어 있으므로 별도 작업 불필요)
      // 페이지 새로고침으로 로컬스토리지 데이터만 표시되도록 함
      window.location.reload();
    } catch (error) {
      console.error('로그아웃 오류:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const webauthnLogin = useCallback(async (username: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.webauthnLogin(username);
      
      if (result.success && result.user) {
        console.log('WebAuthn 로그인 성공:', result.user);
        
        // 로컬스토리지에 데이터가 있는지 확인 (빈 객체는 제외)
        const localInvestments = localStorageUtils.getInvestments();
        const localPortfolioSettings = localStorageUtils.getPortfolioSettings();
        const localAssetSettings = localStorageUtils.getAssetSettings();
        const localFixedIncomes = localStorageUtils.getFixedIncomes();
        const localFixedExpenses = localStorageUtils.getFixedExpenses();
        const localMonthlyBalances = localStorageUtils.getMonthlyBalances();
        
        const hasLocalData = 
          localInvestments.length > 0 ||
          hasMeaningfulSettings(localPortfolioSettings) ||
          hasMeaningfulSettings(localAssetSettings) ||
          localFixedIncomes.length > 0 ||
          localFixedExpenses.length > 0 ||
          localMonthlyBalances.length > 0;
        
        if (hasLocalData) {
          // 로컬 데이터가 있으면 동기화 확인 모달 표시
          // 사용자 정보는 먼저 설정 (모달 표시를 위해)
          setUser(result.user);
          setIsAuthenticated(true);
          setIsInitialized(true);
          
          // 모달에서 동기화 여부를 결정할 때까지 대기
          return new Promise<boolean>((resolve) => {
            setPendingLoginResolve(() => resolve);
            setShowSyncModal(true);
          });
        }
        
        // 로컬 데이터가 없으면 바로 로그인 완료
        setUser(result.user);
        setIsAuthenticated(true);
        setIsInitialized(true);
        return true;
      } else {
        setError(result.error || 'WebAuthn 로그인에 실패했습니다.');
        return false;
      }
    } catch (error: any) {
      console.error('WebAuthn 로그인 오류:', error);
      setError('WebAuthn 로그인 중 오류가 발생했습니다.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const webauthnRegister = useCallback(async (userData: {
    username: string;
    email: string;
    name: string;
    nickname: string;
    phone?: string;
    password?: string;
    deviceName?: string;
  }): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.webauthnRegister(userData);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || 'WebAuthn 회원가입에 실패했습니다.');
        return false;
      }
    } catch (error: any) {
      console.error('WebAuthn 회원가입 오류:', error);
      setError('WebAuthn 회원가입 중 오류가 발생했습니다.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshUser = useCallback((updatedUser?: User) => {
    if (updatedUser) {
      // 업데이트된 사용자 정보 직접 설정
      setUser(updatedUser);
      authService.updateUser(updatedUser);
    } else {
      // 토큰에서 최신 사용자 정보 가져오기
      if (authService.isAuthenticated()) {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        }
      }
    }
  }, []);

  // 동기화 확인 모달 처리
  const handleSyncConfirm = useCallback(async () => {
    setShowSyncModal(false);
    
    try {
      // 로컬스토리지 데이터를 DB로 동기화
      const { syncLocalDataToDB } = await import('../utils/syncLocalToDB');
      await syncLocalDataToDB();
      
      // 동기화 완료 후 로컬스토리지 데이터 삭제 (개인정보 보호)
      localStorageUtils.clearAll();
      
      console.log('로컬 데이터 동기화 완료 및 삭제');
    } catch (syncError) {
      console.error('로컬스토리지 데이터 동기화 실패:', syncError);
    }
    
    // 로그인 성공 처리
    if (pendingLoginResolve) {
      pendingLoginResolve(true);
      setPendingLoginResolve(null);
    }
  }, [pendingLoginResolve]);

  const handleSyncCancel = useCallback(() => {
    setShowSyncModal(false);
    
    // 동기화하지 않으면 로컬 데이터는 그대로 유지
    // 로그인한 계정의 데이터만 표시 (로컬 데이터는 표시하지 않음)
    console.log('로컬 데이터 동기화 취소 - 로컬 데이터는 유지하되 표시하지 않음');
    
    // 로그인 성공 처리
    if (pendingLoginResolve) {
      pendingLoginResolve(true);
      setPendingLoginResolve(null);
    }
  }, [pendingLoginResolve]);

  // 로컬 데이터 개수 계산 (빈 객체/빈 배열은 "데이터 없음"으로 처리)
  const getLocalDataCount = useCallback(() => {
    const localInvestments = localStorageUtils.getInvestments();
    const localPortfolioSettings = localStorageUtils.getPortfolioSettings();
    const localAssetSettings = localStorageUtils.getAssetSettings();
    const localFixedIncomes = localStorageUtils.getFixedIncomes();
    const localFixedExpenses = localStorageUtils.getFixedExpenses();
    const localMonthlyBalances = localStorageUtils.getMonthlyBalances();

    return {
      investments: localInvestments.length,
      portfolioSettings: hasMeaningfulSettings(localPortfolioSettings),
      assetSettings: hasMeaningfulSettings(localAssetSettings),
      fixedIncomes: localFixedIncomes.length,
      fixedExpenses: localFixedExpenses.length,
      monthlyBalances: localMonthlyBalances.length
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isInitialized,
        error,
        login,
        register,
        webauthnLogin,
        webauthnRegister,
        logout,
        clearError,
        refreshUser
      }}
    >
      {children}
      <SyncConfirmModal
        isOpen={showSyncModal}
        localDataCount={getLocalDataCount()}
        onConfirm={handleSyncConfirm}
        onCancel={handleSyncCancel}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


